import { compose } from 'redux';
import { Box, Grid } from '@material-ui/core';
import { useEffect, memo, useMemo, useState, useRef, useCallback } from 'react';
import { connect, useSelector } from 'react-redux';
import withStyles from '@material-ui/styles/withStyles';

import { BasePage } from 'altus-ui-components';

import {
  getFieldsFromState,
  getAvailableWellStatuses,
  getWellboresPageParamsFromState,
  getWellboresGroupedByFieldFromState,
  getWellboresPageResultsFromState,
} from 'features/wells/wellbore.selectors';

import {
  getSummarizedDataStateFromState,
  getCurrentClientOrganizationIdFromState,
} from 'app/app.selectors';

import {
  EMPTY_MAP,
  EMPTY_LIST,
  EMPTY_STRING,
  PagedQueryParams,
  SystemPermission,
} from 'app/app.constants';

import {
  onLoad,
  updateFilter,
  clearWellbores,
  updateWellAperioFilter,
} from 'features/wells/wellbore.actions';

import { useHeader } from 'app/hooks/useHeader';
import usePagedSearch from 'app/hooks/usePagedSearch';
import { getAllRowsExpanded } from 'utils/table.util';
import { Filters } from 'features/wells/components/WellboresFilter';
import WellboresTable from 'features/wells/components/WellboresTable';
import WellboresFilter from 'features/wells/components/WellboresFilter';
import {
  WELLS_ACTIONS as WELLBORE_ACTIONS,
} from 'features/wells/wells.constants';
import { useSystemPermissions } from 'app/hooks/authorization/useSystemPermissions';
import AperioWellboresTable from 'features/wells/components/AperioWellboresTable';
import { useCurrentOrganizationMapZoomLevel, useCurrentUserOrganizationLocation } from 'app/hooks/useCurrentUser';
import WellIntegrityWellLocations from 'features/wells/integrity/wellLocations/WellIntegrityWellLocations';

const getWellboreSubRows = (row) => {
  if (row.original) return;

  return row.get('wellbores', EMPTY_LIST).valueSeq();
};

const defaultFormValues = {
  [Filters.FIELD_ID]: EMPTY_STRING,
  [Filters.TEXT_SEARCH]: EMPTY_STRING,
  [Filters.WELL_STATUS]: EMPTY_STRING,
  [Filters.ORGANIZATION_ID]: EMPTY_STRING,
};

const pageSize = 50;
const initialPage = 1;

const WellboresContainer = ({
  fields,
  classes,
  breadcrumb,
  pageParams,
  pageResults,
  dispatchOnLoad,
  dispatchOnUnload,
  dispatchUpdateFilter,
  currentClientOrganizationId,
  wellboresByField = EMPTY_MAP,
  availableWellStatuses = EMPTY_LIST,
  dispatchUpdateWellAperioFilter,
}) => {
  useHeader({
    subTitle: breadcrumb,
  });

  const { userPermissions } = useSystemPermissions();
  const currentUserOrganizationLocation = useCurrentUserOrganizationLocation();
  const mapRef = useRef(null);
  const [hoveredWellbore, setHoveredWellbore] = useState(null);
  const [debouncedWb, setDebouncedWb] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const { mapZoomLevel, hoverMapZoomLevel } = useCurrentOrganizationMapZoomLevel();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedWb(hoveredWellbore);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [hoveredWellbore]);

  const handleApiLoaded = useCallback(({ map }) => {
    mapRef.current = map;
    setMapLoaded(true);
  }, []);

  const animateZoom = useCallback(
    (map, from, to, duration = 300, onComplete) => {
      const steps = Math.abs(to - from);
      if (steps === 0) {
        onComplete?.();
        return;
      }
      const stepMs = duration / steps;
      let z = from;
      const dir = to > from ? 1 : -1;

      const iv = window.setInterval(() => {
        z += dir;
        map.setZoom(z);
        if (z === to) {
          window.clearInterval(iv);
          onComplete?.();
        }
      }, stepMs);
    },
    [],
  );

  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const mapDiv = map.getDiv();
    mapDiv
      .querySelectorAll('[data-marker-id]')
      .forEach((el) => el.classList.remove('hovered-marker'));

    if (debouncedWb) {
      const sel = mapDiv.querySelector(
        `[data-marker-id="${debouncedWb.wellboreId}"]`,
      );
      if (sel) sel.classList.add('hovered-marker');
    }

    const targetCenter = debouncedWb
      ? {
        lat: debouncedWb.location.northSouth,
        lng: debouncedWb.location.eastWest,
      }
      : {
        lat: currentUserOrganizationLocation[0],
        lng: currentUserOrganizationLocation[1],
      };

    const targetZoom = debouncedWb ? hoverMapZoomLevel : mapZoomLevel;
    const offsetXpx = debouncedWb ? window.innerWidth / 4 : 0;

    map.panTo(targetCenter);

    const currZ = map.getZoom() ?? mapZoomLevel;
    animateZoom(map, currZ, targetZoom, 300, () => {
      if (offsetXpx) {
        map.panBy(offsetXpx, 0);
      }
    });
  }, [debouncedWb, currentUserOrganizationLocation, animateZoom, mapLoaded, mapZoomLevel, hoverMapZoomLevel]);

  const dataState = useSelector((state) =>
    getSummarizedDataStateFromState(
      state,
      WELLBORE_ACTIONS.GET_FIELDS,
      WELLBORE_ACTIONS.WELLS_PAGE_LOADED,
      WELLBORE_ACTIONS.UPDATE_WELL_FILTER,
    ),
  );

  useEffect(() => {
    dispatchOnLoad(currentClientOrganizationId);

    return () => dispatchOnUnload();
  }, [dispatchOnLoad, dispatchOnUnload, currentClientOrganizationId]);

  const initialTableState = useMemo(
    () => ({
      pageSize,
      pageIndex: (pageParams.get('currentPage') ?? initialPage) - 1,
      expanded: getAllRowsExpanded(wellboresByField, getWellboreSubRows),
    }),
    [pageParams, wellboresByField],
  );

  const initialFilterValues = useMemo(
    () => ({
      ...defaultFormValues,
      organizationId: currentClientOrganizationId,
    }),
    [currentClientOrganizationId],
  );

  const initialPagedSearchFilters = useMemo(
    () => ({
      [PagedQueryParams.PAGE]: initialTableState.pageIndex + 1,
      [PagedQueryParams.PAGE_SIZE]: pageSize,
      ...initialFilterValues,
    }),
    [initialTableState, initialFilterValues],
  );

  const { onPageChange, onFiltersChange } = usePagedSearch(
    userPermissions.includes(SystemPermission.WELL_ACCESS_ONLY)
      ? dispatchUpdateWellAperioFilter
      : dispatchUpdateFilter,
    initialPagedSearchFilters,
  );

  const handleRowHover = useCallback(
    (wellbore) => setHoveredWellbore(wellbore),
    [setHoveredWellbore],
  );
  const handleRowLeave = useCallback(
    () => setHoveredWellbore(null),
    [setHoveredWellbore],
  );

  return (
    <BasePage
      dataState={dataState}
      classes={{
        children: classes.basePageChildren,
      }}
    >
      <WellIntegrityWellLocations
        wellbores={pageResults}
        handleApiLoaded={handleApiLoaded}
      />
      <Grid container className={classes.content} justifyContent="flex-end">
        <Grid
          item
          sm={7}
          xs={12}
          container
          component={Box}
          paddingLeft={2}
          paddingRight={2}
          direction="column"
        >
          <WellboresFilter
            fields={fields}
            onSubmit={onFiltersChange}
            initialValues={initialFilterValues}
            wellStatuses={availableWellStatuses}
          />
          {userPermissions.includes(SystemPermission.WELL_ACCESS_ONLY) ? (
            <AperioWellboresTable
              initialState={initialTableState}
              onPageChange={onPageChange}
              getSubRows={getWellboreSubRows}
              wellboresByField={wellboresByField}
              pageCount={pageParams.get('pageCount')}
              onMouseEnter={handleRowHover}
              onMouseLeave={handleRowLeave}
            />
          ) : (
            <WellboresTable
              initialState={initialTableState}
              onPageChange={onPageChange}
              getSubRows={getWellboreSubRows}
              wellboresByField={wellboresByField}
              pageCount={pageParams.get('pageCount')}
              onMouseEnter={handleRowHover}
              onMouseLeave={handleRowLeave}
            />
          )}
        </Grid>
      </Grid>
    </BasePage>
  );
};

const styles = (theme) => ({
  basePageChildren: {
    padding: 0,
  },
  content: {
    top: 0,
    bottom: 0,
    overflow: 'auto',
    position: 'absolute',
  },
  '@global': {
    '.hovered-marker': {
      color: `${theme.palette.status[2]} !important`,
      transform: 'scale(1.5) !important',
      transition: 'transform 0.2s ease-in-out, color 0.2s ease-in-out',
      transformOrigin: 'center bottom',
    },
  },
});

export default compose(
  connect(
    (state) => ({
      fields: getFieldsFromState(state),
      wellboresByField: getWellboresGroupedByFieldFromState(state),
      availableWellStatuses: getAvailableWellStatuses(state),
      pageParams: getWellboresPageParamsFromState(state),
      pageResults: getWellboresPageResultsFromState(state),
      currentClientOrganizationId:
        getCurrentClientOrganizationIdFromState(state),
    }),
    {
      dispatchOnLoad: onLoad,
      dispatchOnUnload: clearWellbores,
      dispatchUpdateFilter: updateFilter,
      dispatchUpdateWellAperioFilter: updateWellAperioFilter,
    },
  ),
  memo,
  withStyles(styles),
)(WellboresContainer);
